import { useEffect, useState } from "react";
import axios from "axios";
import { Copy, ShieldCheck, Clock3 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Official pricing from the studio owner.
const inclusions = [
  "550 sqft studio space",
  "6 curated studio walls & setups",
  "4 movable backdrops",
  "Chairs & selected props",
  "Separate makeup & changing room",
  "Fully air-conditioned studio",
  "Pantry area",
  "Washroom",
  "Clothing rack",
  "High-speed Wi-Fi",
  "Mini refrigerator",
  "Bluetooth speaker",
];

export const Packages = () => {
  const [vouchers, setVouchers] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/vouchers`)
      .then((res) => setVouchers(res.data.vouchers))
      .catch(() => setVouchers([]));
  }, []);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Voucher ${code} copied`, {
        description: "Paste it into the booking form to claim your discount.",
      });
    } catch {
      toast.info(`Your voucher code: ${code}`);
    }
  };

  return (
    <>
      <section id="plans" data-testid="packages-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="label-caps text-forest block mb-2">Studio Rental</span>
            <h2 data-testid="packages-heading" className="heavy-heading text-3xl sm:text-4xl font-extrabold text-ink mb-4">
              Simple, Honest Pricing
            </h2>
            <p className="text-sm text-muted-g">
              A thoughtfully curated studio space designed for photo shoots, video shoots,
              content creation and more.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Standard Rental */}
            <article
              data-testid="package-card-standard"
              className="lg:col-span-2 bg-forest text-white rounded-xl border border-forest-dark shadow-lg p-8 sm:p-10"
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-sage">Standard Rental</span>
              <div className="flex flex-wrap items-baseline gap-3 mt-3 mb-2">
                <span data-testid="package-price-standard" className="text-5xl font-extrabold tracking-tight">₹1,100</span>
                <span className="text-sm text-sage">/ hour</span>
              </div>
              <p className="text-xs text-[#D0D7CC] mb-8">Minimum booking: 2 hours</p>
              <p className="label-caps text-sage mb-4">Your booking includes</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm font-medium text-[#D0D7CC] mb-10">
                {inclusions.map((f) => (
                  <li key={f}>✓ {f}</li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid="package-book-standard"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-white text-forest label-caps rounded-lg hover:bg-surface-2 transition-colors duration-200 shadow-sm"
              >
                Book the Studio
              </a>
            </article>

            <div className="space-y-6">
              {/* Additional Time */}
              <article data-testid="package-card-additional" className="bg-bg rounded-xl border border-line p-8">
                <Clock3 size={22} strokeWidth={1.75} className="text-forest mb-4" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-g">Additional Time</span>
                <div className="flex items-baseline gap-2 mt-2 mb-3">
                  <span data-testid="package-price-additional" className="text-3xl font-extrabold text-ink">₹700</span>
                  <span className="text-xs text-muted-g">/ hour</span>
                </div>
                <p className="text-sm text-muted-gd leading-relaxed">
                  Need a little more time? Additional studio time can be extended at ₹700 per hour,
                  subject to availability.
                </p>
              </article>

              {/* Security Deposit */}
              <article data-testid="package-card-deposit" className="bg-bg rounded-xl border border-line p-8">
                <ShieldCheck size={22} strokeWidth={1.75} className="text-forest mb-4" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-g">Security Deposit</span>
                <div className="flex items-baseline gap-2 mt-2 mb-3">
                  <span data-testid="package-price-deposit" className="text-3xl font-extrabold text-ink">₹5,000</span>
                  <span className="text-xs text-muted-g">refundable</span>
                </div>
                <p className="text-sm text-muted-gd leading-relaxed">
                  A refundable security deposit of ₹5,000 is required for all studio bookings and will be
                  refunded post-shoot, subject to no damage to the studio, equipment, props or backdrops.
                </p>
              </article>
            </div>
          </div>

          <p data-testid="booking-arrival-note" className="text-center text-sm text-muted-g mt-10 max-w-2xl mx-auto">
            Please arrive on time and plan your setup and pack-up within your booked slot.
            We recommend booking sufficient time to ensure a comfortable, uninterrupted shoot.
          </p>
        </div>
      </section>

      <section id="promo" data-testid="vouchers-section" className="py-14 bg-forest text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {vouchers.map((v) => (
            <article
              key={v.code}
              data-testid={`voucher-card-${v.code}`}
              className="bg-forest-dark rounded-xl border border-[#2D5243] p-8 flex flex-col md:flex-row items-center justify-between gap-6"
            >
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-sage bg-forest px-2.5 py-1 rounded">
                  {v.title}
                </span>
                <h3 className="text-2xl font-extrabold text-white mt-2">{v.percent_off}% Discount Voucher</h3>
                <p className="text-sm text-[#D0D7CC] mt-1">
                  {v.description} Apply code{" "}
                  <code data-testid={`voucher-code-${v.code}`} className="font-mono font-bold text-white bg-forest px-2 py-0.5 rounded">
                    {v.code}
                  </code>{" "}
                  with your reservation.
                </p>
              </div>
              <button
                type="button"
                onClick={() => copyCode(v.code)}
                data-testid={`voucher-copy-${v.code}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-surface-2 text-forest label-caps rounded-lg transition-colors duration-200 shrink-0"
              >
                <Copy size={14} /> Copy code
              </button>
            </article>
          ))}
        </div>
      </section>
    </>
  );
};
